import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, Chip, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, CircularProgress, Alert,
    Card, CardMedia, CardContent, Grid, Avatar, Tooltip, useTheme, useMediaQuery,
    AppBar, Toolbar, Fade, Backdrop
} from '@mui/material';
import {
    CheckCircle, Cancel, Visibility, Person, Store, Refresh, Pending
} from '@mui/icons-material';
import adminService from '../services/adminService';

const API_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://xaintro-backend.onrender.com';

const PendingProducts = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const [loading, setLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [viewDialog, setViewDialog] = useState(false);
    const [fullScreenView, setFullScreenView] = useState(false);
    const [rejectDialog, setRejectDialog] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [sellerDetails, setSellerDetails] = useState(null);
    const [sellerLoading, setSellerLoading] = useState(false);

    const loadProducts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await adminService.getPendingProducts();
            setProducts(response.products || []);
        } catch (err) {
            console.error('Failed to load pending products:', err);
            setError('Failed to load pending products');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadProducts();
    }, [loadProducts]);

    const loadSellerDetails = async (sellerId) => {
        if (!sellerId) return;
        setSellerLoading(true);
        try {
            const response = await adminService.getSellerDetails(sellerId);
            setSellerDetails(response.seller);
        } catch (err) {
            console.error('Failed to load seller details:', err);
        } finally {
            setSellerLoading(false);
        }
    };

    const handleViewProduct = (product) => {
        setSelectedProduct(product);
        setSellerDetails(null); // Reset seller details
        if (product.seller?._id) {
            loadSellerDetails(product.seller._id);
        }
        if (isMobile) {
            setFullScreenView(true);
        } else {
            setViewDialog(true);
        }
    };

    const handleCloseView = () => {
        setViewDialog(false);
        setFullScreenView(false);
        setSelectedProduct(null);
    };

    const handleApprove = async (product) => {
        setActionLoading(true);
        try {
            await adminService.verifyProduct(product._id, 'approve');
            setProducts(products.filter(p => p._id !== product._id));
            setSuccess(`Product "${product.name}" approved successfully!`);
            setViewDialog(false);
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError('Failed to approve product');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRejectClick = (product) => {
        setSelectedProduct(product);
        setRejectionReason('');
        setRejectDialog(true);
    };

    const handleRejectConfirm = async () => {
        if (!selectedProduct) return;

        setActionLoading(true);
        try {
            await adminService.verifyProduct(selectedProduct._id, 'reject', rejectionReason);
            setProducts(products.filter(p => p._id !== selectedProduct._id));
            setSuccess(`Product "${selectedProduct.name}" rejected.`);
            setRejectDialog(false);
            setViewDialog(false);
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError('Failed to reject product');
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 0 },
                mb: 3 
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Pending sx={{ fontSize: { xs: 28, sm: 32 }, color: '#FF9800' }} />
                    <Box>
                        <Typography variant={isMobile ? "h6" : "h5"} fontWeight={700}>
                            Pending Products
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Review and approve seller products
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip
                        label={`${products.length} Pending`}
                        color="warning"
                        size={isMobile ? "small" : "medium"}
                        sx={{ fontWeight: 600 }}
                    />
                    <IconButton onClick={loadProducts} size={isMobile ? "small" : "medium"}>
                        <Refresh />
                    </IconButton>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

            {products.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <CheckCircle sx={{ fontSize: 64, color: '#4CAF50', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        No pending products to review!
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        All products have been reviewed.
                    </Typography>
                </Paper>
            ) : (
                <>
                    {/* Mobile Card View */}
                    {isMobile ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2, px: 1 }}>
                            {products.map((product) => (
                                <Box key={product._id} sx={{ flex: { xs: '0 0 calc(50% - 4px)', sm: '0 0 calc(50% - 4px)' }, maxWidth: { xs: 200, sm: 220 } }}>
                                    <Card sx={{ borderRadius: 3, boxShadow: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <CardContent sx={{ p: 2, flex: 1 }}>
                                        {/* Product Header */}
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                                            <Avatar
                                                variant="rounded"
                                                src={product.images?.[0] ?
                                                    (product.images[0].startsWith('http') ? product.images[0] : `${API_URL}${product.images[0]}`)
                                                    : undefined}
                                                sx={{ width: 50, height: 50, flexShrink: 0 }}
                                            >
                                                <Store sx={{ fontSize: 24 }} />
                                            </Avatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                                                    {product.name.length > 25 ? product.name.substring(0, 25) + '...' : product.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                                    Stock: {product.stock || 0}
                                                </Typography>
                                                <Chip 
                                                    label={product.category?.name || 'N/A'} 
                                                    size="small" 
                                                    color="primary" 
                                                    variant="outlined"
                                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                                />
                                            </Box>
                                        </Box>

                                        {/* Price Section */}
                                        <Box sx={{ mb: 1.5 }}>
                                            <Typography variant="subtitle1" color="primary" fontWeight={700} sx={{ fontSize: '0.9rem' }}>
                                                PKR {product.price?.toLocaleString()}
                                            </Typography>
                                            {product.originalPrice && product.originalPrice > product.price && (
                                                <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through', display: 'block' }}>
                                                    PKR {product.originalPrice?.toLocaleString()}
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* Seller Info */}
                                        <Paper sx={{ p: 1, bgcolor: '#f8f9fa', mb: 1.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Avatar sx={{ width: 28, height: 28, bgcolor: '#FF5722' }}>
                                                    <Person sx={{ fontSize: 14 }} />
                                                </Avatar>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="caption" fontWeight={600} sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                        {(product.seller?.brandName || product.seller?.name || 'Unknown').length > 15 
                                                            ? (product.seller?.brandName || product.seller?.name || 'Unknown').substring(0, 15) + '...' 
                                                            : (product.seller?.brandName || product.seller?.name || 'Unknown')}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                        {product.seller?.email && product.seller.email.length > 20 
                                                            ? product.seller.email.substring(0, 20) + '...' 
                                                            : product.seller?.email}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Paper>

                                        {/* Action Buttons */}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            <Button
                                                variant="contained"
                                                color="success"
                                                size="small"
                                                startIcon={<CheckCircle />}
                                                onClick={() => handleApprove(product)}
                                                sx={{ fontSize: '0.7rem', py: 0.5 }}
                                            >
                                                Approve
                                            </Button>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<Visibility />}
                                                    onClick={() => handleViewProduct(product)}
                                                    sx={{ flex: 1, fontSize: '0.7rem', py: 0.5, minWidth: 'auto' }}
                                                >
                                                    View
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    size="small"
                                                    startIcon={<Cancel />}
                                                    onClick={() => handleRejectClick(product)}
                                                    sx={{ flex: 1, fontSize: '0.7rem', py: 0.5, minWidth: 'auto' }}
                                                >
                                                    Reject
                                                </Button>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        /* Desktop Table View */
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                        <TableCell>Product</TableCell>
                                        <TableCell>Seller</TableCell>
                                        <TableCell>Price</TableCell>
                                        <TableCell>Category</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {products.map((product) => (
                                        <TableRow key={product._id} hover>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Avatar
                                                        variant="rounded"
                                                        src={product.images?.[0] ?
                                                            (product.images[0].startsWith('http') ? product.images[0] : `${API_URL}${product.images[0]}`)
                                                            : undefined}
                                                        sx={{ width: 50, height: 50 }}
                                                    >
                                                        <Store />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="subtitle2" fontWeight={600}>
                                                            {product.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Stock: {product.stock || 0}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Avatar sx={{ width: 32, height: 32, bgcolor: '#FF5722' }}>
                                                        <Person sx={{ fontSize: 18 }} />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {product.seller?.brandName || product.seller?.name || 'Unknown'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {product.seller?.email}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography fontWeight={600}>
                                                    PKR {product.price?.toLocaleString()}
                                                </Typography>
                                                {product.originalPrice && product.originalPrice > product.price && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                                                        PKR {product.originalPrice?.toLocaleString()}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={product.category?.name || 'N/A'} size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {new Date(product.createdAt).toLocaleDateString()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="View Details">
                                                    <IconButton onClick={() => handleViewProduct(product)} color="primary">
                                                        <Visibility />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Approve">
                                                    <IconButton onClick={() => handleApprove(product)} color="success">
                                                        <CheckCircle />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Reject">
                                                    <IconButton onClick={() => handleRejectClick(product)} color="error">
                                                        <Cancel />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </>
            )}

            {/* View Product Dialog - Desktop */}
            <Dialog open={viewDialog} onClose={handleCloseView} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight={700}>Product Details</Typography>
                    <Chip label="Pending Review" color="warning" />
                </DialogTitle>
                <DialogContent dividers>
                    {selectedProduct && (
                        <Grid container spacing={3}>
                            {/* Images */}
                            <Grid item xs={12} md={5}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {selectedProduct.images?.length > 0 ? (
                                        <>
                                            <Card>
                                                <CardMedia
                                                    component="img"
                                                    height="250"
                                                    image={selectedProduct.images[0].startsWith('http') ?
                                                        selectedProduct.images[0] : `${API_URL}${selectedProduct.images[0]}`}
                                                    alt={selectedProduct.name}
                                                    sx={{ objectFit: 'contain', bgcolor: '#f5f5f5' }}
                                                />
                                            </Card>
                                            {selectedProduct.images.length > 1 && (
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    {selectedProduct.images.slice(1).map((img, idx) => (
                                                        <Avatar
                                                            key={idx}
                                                            variant="rounded"
                                                            src={img.startsWith('http') ? img : `${API_URL}${img}`}
                                                            sx={{ width: 60, height: 60 }}
                                                        />
                                                    ))}
                                                </Box>
                                            )}
                                        </>
                                    ) : (
                                        <Paper sx={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Typography color="text.secondary">No images</Typography>
                                        </Paper>
                                    )}

                                    {/* Product Video */}
                                    {selectedProduct.video && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                                Product Video
                                            </Typography>
                                            <video
                                                src={selectedProduct.video.startsWith('http') ?
                                                    selectedProduct.video : `${API_URL}${selectedProduct.video}`}
                                                controls
                                                style={{ width: '100%', maxHeight: 200, borderRadius: 8 }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Grid>

                            {/* Details */}
                            <Grid item xs={12} md={7}>
                                <Typography variant="h5" fontWeight={700} gutterBottom>
                                    {selectedProduct.name}
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                    <Typography variant="h5" color="primary" fontWeight={700}>
                                        PKR {selectedProduct.price?.toLocaleString()}
                                    </Typography>
                                    {selectedProduct.originalPrice && selectedProduct.originalPrice > selectedProduct.price && (
                                        <Typography variant="h6" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                                            PKR {selectedProduct.originalPrice?.toLocaleString()}
                                        </Typography>
                                    )}
                                </Box>

                                <Typography variant="body2" color="text.secondary" paragraph>
                                    {selectedProduct.description}
                                </Typography>

                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                    <Chip label={`Category: ${selectedProduct.category?.name || 'N/A'}`} size="small" />
                                    <Chip label={`Brand: ${selectedProduct.brand || 'N/A'}`} size="small" />
                                    <Chip label={`Stock: ${selectedProduct.stock || 0}`} size="small" />
                                </Box>

                                {/* Seller Info */}
                                <Paper sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                        Seller Information
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar sx={{ bgcolor: '#FF5722' }}>
                                            <Person />
                                        </Avatar>
                                        <Box>
                                            <Typography fontWeight={600}>
                                                {selectedProduct.seller?.brandName || selectedProduct.seller?.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {selectedProduct.seller?.email}
                                            </Typography>
                                            {selectedProduct.seller?.phone && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Phone: {selectedProduct.seller.phone}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={handleCloseView} color="inherit">
                        Close
                    </Button>
                    <Button
                        onClick={() => handleRejectClick(selectedProduct)}
                        variant="outlined"
                        color="error"
                        startIcon={<Cancel />}
                    >
                        Reject
                    </Button>
                    <Button
                        onClick={() => handleApprove(selectedProduct)}
                        variant="contained"
                        color="success"
                        startIcon={actionLoading ? <CircularProgress size={20} /> : <CheckCircle />}
                        disabled={actionLoading}
                    >
                        Approve
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Full Screen Mobile Product View */}
            <Dialog
                open={fullScreenView}
                onClose={handleCloseView}
                fullScreen
                TransitionComponent={Fade}
            >
                <AppBar sx={{ position: 'relative', bgcolor: '#FF9800' }}>
                    <Toolbar>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={handleCloseView}
                            aria-label="close"
                        >
                            <Cancel />
                        </IconButton>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                            Product Details
                        </Typography>
                        <Chip 
                            label="Pending Review" 
                            color="warning" 
                            size="small"
                            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                        />
                    </Toolbar>
                </AppBar>
                
                {selectedProduct && (
                    <Box sx={{ p: 2, pt: 1 }}>
                        {/* Product Images Gallery */}
                        <Card sx={{ mb: 3, borderRadius: 3 }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography variant="h6" fontWeight={600} gutterBottom>
                                    📸 Product Images
                                </Typography>
                                {selectedProduct.images?.length > 0 ? (
                                    <Box>
                                        {/* Main Image */}
                                        <CardMedia
                                            component="img"
                                            height="250"
                                            image={selectedProduct.images[0].startsWith('http') ?
                                                selectedProduct.images[0] : `${API_URL}${selectedProduct.images[0]}`}
                                            alt={selectedProduct.name}
                                            sx={{ objectFit: 'contain', bgcolor: '#f5f5f5', borderRadius: 2, mb: 2 }}
                                        />
                                        {/* Additional Images */}
                                        {selectedProduct.images.length > 1 && (
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                {selectedProduct.images.slice(1).map((img, idx) => (
                                                    <Avatar
                                                        key={idx}
                                                        variant="rounded"
                                                        src={img.startsWith('http') ? img : `${API_URL}${img}`}
                                                        sx={{ width: 80, height: 80, cursor: 'pointer' }}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                ) : (
                                    <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', borderRadius: 2 }}>
                                        <Typography color="text.secondary">No images available</Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>

                        {/* Complete Product Information */}
                        <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                            <Typography variant="h6" fontWeight={700} gutterBottom>
                                🛍️ Product Information
                            </Typography>
                            
                            {/* Basic Info */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h5" fontWeight={700} color="primary" gutterBottom>
                                    {selectedProduct.name}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                    <Typography variant="h4" color="primary" fontWeight={700}>
                                        PKR {selectedProduct.price?.toLocaleString()}
                                    </Typography>
                                    {selectedProduct.originalPrice && selectedProduct.originalPrice > selectedProduct.price && (
                                        <Typography variant="body1" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                                            PKR {selectedProduct.originalPrice?.toLocaleString()}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            {/* Description */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                    Description
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {selectedProduct.description || 'No description available'}
                                </Typography>
                            </Box>

                            {/* Complete Product Details Grid */}
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={6} sm={3}>
                                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary">Stock</Typography>
                                        <Typography variant="h6" fontWeight={700}>{selectedProduct.stock || 0}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary">Category</Typography>
                                        <Typography variant="h6" fontWeight={700}>{selectedProduct.category?.name || 'N/A'}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary">Brand</Typography>
                                        <Typography variant="h6" fontWeight={700}>{selectedProduct.brand || 'N/A'}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary">Status</Typography>
                                        <Chip label="Pending" color="warning" size="small" />
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary">Created</Typography>
                                        <Typography variant="h6" fontWeight={700}>{new Date(selectedProduct.createdAt).toLocaleDateString()}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary">SKU</Typography>
                                        <Typography variant="h6" fontWeight={700}>{selectedProduct.sku || 'N/A'}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary">Condition</Typography>
                                        <Typography variant="h6" fontWeight={700}>{selectedProduct.condition || 'New'}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary">Views</Typography>
                                        <Typography variant="h6" fontWeight={700}>{selectedProduct.views || 0}</Typography>
                                    </Box>
                                </Grid>
                            </Grid>

                            {/* Additional Product Information */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                    📊 Additional Information
                                </Typography>
                                <Grid container spacing={2}>
                                    {selectedProduct.subcategory && (
                                        <Grid item xs={6} sm={4}>
                                            <Typography variant="caption" color="text.secondary">Subcategory</Typography>
                                            <Typography variant="body2">{selectedProduct.subcategory}</Typography>
                                        </Grid>
                                    )}
                                    {selectedProduct.color && (
                                        <Grid item xs={6} sm={4}>
                                            <Typography variant="caption" color="text.secondary">Color</Typography>
                                            <Typography variant="body2">{selectedProduct.color}</Typography>
                                        </Grid>
                                    )}
                                    {selectedProduct.size && (
                                        <Grid item xs={6} sm={4}>
                                            <Typography variant="caption" color="text.secondary">Size</Typography>
                                            <Typography variant="body2">{selectedProduct.size}</Typography>
                                        </Grid>
                                    )}
                                    {selectedProduct.material && (
                                        <Grid item xs={6} sm={4}>
                                            <Typography variant="caption" color="text.secondary">Material</Typography>
                                            <Typography variant="body2">{selectedProduct.material}</Typography>
                                        </Grid>
                                    )}
                                    {selectedProduct.model && (
                                        <Grid item xs={6} sm={4}>
                                            <Typography variant="caption" color="text.secondary">Model</Typography>
                                            <Typography variant="body2">{selectedProduct.model}</Typography>
                                        </Grid>
                                    )}
                                    {selectedProduct.weight && (
                                        <Grid item xs={6} sm={4}>
                                            <Typography variant="caption" color="text.secondary">Weight</Typography>
                                            <Typography variant="body2">{selectedProduct.weight}</Typography>
                                        </Grid>
                                    )}
                                    {selectedProduct.dimensions && (
                                        <Grid item xs={6} sm={4}>
                                            <Typography variant="caption" color="text.secondary">Dimensions</Typography>
                                            <Typography variant="body2">{selectedProduct.dimensions}</Typography>
                                        </Grid>
                                    )}
                                    {selectedProduct.manufacturer && (
                                        <Grid item xs={6} sm={4}>
                                            <Typography variant="caption" color="text.secondary">Manufacturer</Typography>
                                            <Typography variant="body2">{selectedProduct.manufacturer}</Typography>
                                        </Grid>
                                    )}
                                    {selectedProduct.warranty && (
                                        <Grid item xs={6} sm={4}>
                                            <Typography variant="caption" color="text.secondary">Warranty</Typography>
                                            <Typography variant="body2">{selectedProduct.warranty}</Typography>
                                        </Grid>
                                    )}
                                    {selectedProduct.origin && (
                                        <Grid item xs={6} sm={4}>
                                            <Typography variant="caption" color="text.secondary">Origin</Typography>
                                            <Typography variant="body2">{selectedProduct.origin}</Typography>
                                        </Grid>
                                    )}
                                    {selectedProduct.language && (
                                        <Grid item xs={6} sm={4}>
                                            <Typography variant="caption" color="text.secondary">Language</Typography>
                                            <Typography variant="body2">{selectedProduct.language}</Typography>
                                        </Grid>
                                    )}
                                    {selectedProduct.ageGroup && (
                                        <Grid item xs={6} sm={4}>
                                            <Typography variant="caption" color="text.secondary">Age Group</Typography>
                                            <Typography variant="body2">{selectedProduct.ageGroup}</Typography>
                                        </Grid>
                                    )}
                                    {selectedProduct.gender && (
                                        <Grid item xs={6} sm={4}>
                                            <Typography variant="caption" color="text.secondary">Gender</Typography>
                                            <Typography variant="body2">{selectedProduct.gender}</Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>

                            {/* Specifications */}
                            {selectedProduct.specifications && (
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                        📋 Specifications
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {selectedProduct.specifications.weight && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Weight/Volume</Typography>
                                                <Typography variant="body2">{selectedProduct.specifications.weight}</Typography>
                                            </Grid>
                                        )}
                                        {selectedProduct.specifications.dimensions && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Dimensions</Typography>
                                                <Typography variant="body2">{selectedProduct.specifications.dimensions}</Typography>
                                            </Grid>
                                        )}
                                        {selectedProduct.specifications.material && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Material</Typography>
                                                <Typography variant="body2">{selectedProduct.specifications.material}</Typography>
                                            </Grid>
                                        )}
                                        {selectedProduct.specifications.origin && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Origin</Typography>
                                                <Typography variant="body2">{selectedProduct.specifications.origin}</Typography>
                                            </Grid>
                                        )}
                                        {selectedProduct.specifications.warranty && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Warranty</Typography>
                                                <Typography variant="body2">{selectedProduct.specifications.warranty}</Typography>
                                            </Grid>
                                        )}
                                        {selectedProduct.specifications.ingredients && (
                                            <Grid item xs={12}>
                                                <Typography variant="caption" color="text.secondary">Ingredients</Typography>
                                                <Typography variant="body2">{selectedProduct.specifications.ingredients}</Typography>
                                            </Grid>
                                        )}
                                        {selectedProduct.specifications.directions && (
                                            <Grid item xs={12}>
                                                <Typography variant="caption" color="text.secondary">Directions</Typography>
                                                <Typography variant="body2">{selectedProduct.specifications.directions}</Typography>
                                            </Grid>
                                        )}
                                        {selectedProduct.specifications.precautions && (
                                            <Grid item xs={12}>
                                                <Typography variant="caption" color="text.secondary">Precautions</Typography>
                                                <Typography variant="body2">{selectedProduct.specifications.precautions}</Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Box>
                            )}

                            {/* Variants */}
                            {selectedProduct.variantGroups && selectedProduct.variantGroups.length > 0 && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                        🎨 Product Variants
                                    </Typography>
                                    {selectedProduct.variantGroups.map((group, index) => (
                                        <Box key={index} sx={{ mb: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                                {group.name}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                {group.options.map((option, optIndex) => (
                                                    <Chip
                                                        key={optIndex}
                                                        label={typeof option === 'string' ? option : option.value || 'Option'}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ mr: 1, mb: 1 }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Paper>

                        {/* Complete Seller Information */}
                        <Paper sx={{ p: 3, mb: 3, borderRadius: 3, bgcolor: '#f8f9fa' }}>
                            <Typography variant="h6" fontWeight={700} gutterBottom>
                                👤 Seller Information
                            </Typography>
                            
                            {sellerLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                    <CircularProgress />
                                </Box>
                            ) : sellerDetails ? (
                                <Box>
                                    {/* Seller Profile */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                        <Avatar sx={{ bgcolor: '#FF5722', width: 64, height: 64 }}>
                                            <Person sx={{ fontSize: 32 }} />
                                        </Avatar>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="h6" fontWeight={700}>
                                                {sellerDetails.brandName || sellerDetails.name || 'Unknown'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {sellerDetails.email}
                                            </Typography>
                                            {sellerDetails.phone && (
                                                <Typography variant="body2" color="text.secondary">
                                                    📱 {sellerDetails.phone}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Seller Plan */}
                                    {sellerDetails.plan && (
                                        <Box sx={{ mb: 3, p: 2, bgcolor: '#fff3e0', borderRadius: 2, border: '1px solid #ffb74d' }}>
                                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                                💎 Current Plan
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={6} sm={3}>
                                                    <Typography variant="caption" color="text.secondary">Plan Type</Typography>
                                                    <Typography variant="h6" fontWeight={700} color="#e65100">
                                                        {sellerDetails.plan.type || 'Basic'}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6} sm={3}>
                                                    <Typography variant="caption" color="text.secondary">Status</Typography>
                                                    <Chip 
                                                        label={sellerDetails.plan.status || 'Active'} 
                                                        color="success" 
                                                        size="small" 
                                                    />
                                                </Grid>
                                                <Grid item xs={6} sm={3}>
                                                    <Typography variant="caption" color="text.secondary">Products Limit</Typography>
                                                    <Typography variant="h6" fontWeight={700}>
                                                        {sellerDetails.plan.productLimit || 'Unlimited'}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6} sm={3}>
                                                    <Typography variant="caption" color="text.secondary">Expires</Typography>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {sellerDetails.plan.expiresAt ? 
                                                            new Date(sellerDetails.plan.expiresAt).toLocaleDateString() : 
                                                            'Lifetime'
                                                        }
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    )}

                                    {/* Business Information */}
                                    {sellerDetails.business && (
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                                🏢 Business Information
                                            </Typography>
                                            <Grid container spacing={2}>
                                                {sellerDetails.business.businessType && (
                                                    <Grid item xs={6} sm={4}>
                                                        <Typography variant="caption" color="text.secondary">Business Type</Typography>
                                                        <Typography variant="body2">{sellerDetails.business.businessType}</Typography>
                                                    </Grid>
                                                )}
                                                {sellerDetails.business.address && (
                                                    <Grid item xs={12}>
                                                        <Typography variant="caption" color="text.secondary">Address</Typography>
                                                        <Typography variant="body2">{sellerDetails.business.address}</Typography>
                                                    </Grid>
                                                )}
                                                {sellerDetails.business.city && (
                                                    <Grid item xs={6} sm={4}>
                                                        <Typography variant="caption" color="text.secondary">City</Typography>
                                                        <Typography variant="body2">{sellerDetails.business.city}</Typography>
                                                    </Grid>
                                                )}
                                                {sellerDetails.business.country && (
                                                    <Grid item xs={6} sm={4}>
                                                        <Typography variant="caption" color="text.secondary">Country</Typography>
                                                        <Typography variant="body2">{sellerDetails.business.country}</Typography>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Box>
                                    )}

                                    {/* Seller Stats */}
                                    {sellerDetails.stats && (
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                                📊 Seller Statistics
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={6} sm={3}>
                                                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                                                        <Typography variant="caption" color="text.secondary">Total Products</Typography>
                                                        <Typography variant="h6" fontWeight={700} color="#1976d2">
                                                            {sellerDetails.stats.totalProducts || 0}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={6} sm={3}>
                                                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#e8f5e8', borderRadius: 2 }}>
                                                        <Typography variant="caption" color="text.secondary">Active Products</Typography>
                                                        <Typography variant="h6" fontWeight={700} color="#388e3c">
                                                            {sellerDetails.stats.activeProducts || 0}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={6} sm={3}>
                                                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#fff3e0', borderRadius: 2 }}>
                                                        <Typography variant="caption" color="text.secondary">Pending</Typography>
                                                        <Typography variant="h6" fontWeight={700} color="#f57c00">
                                                            {sellerDetails.stats.pendingProducts || 0}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={6} sm={3}>
                                                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#fce4ec', borderRadius: 2 }}>
                                                        <Typography variant="caption" color="text.secondary">Total Orders</Typography>
                                                        <Typography variant="h6" fontWeight={700} color="#c2185b">
                                                            {sellerDetails.stats.totalOrders || 0}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    )}

                                    {/* Registration Info */}
                                    <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            📅 Member since: {new Date(sellerDetails.createdAt).toLocaleDateString()}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            🔄 Last active: {sellerDetails.lastActive ? 
                                                new Date(sellerDetails.lastActive).toLocaleDateString() : 
                                                'Unknown'
                                            }
                                        </Typography>
                                    </Box>
                                </Box>
                            ) : (
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Unable to load detailed seller information
                                    </Typography>
                                    {/* Basic seller info from product */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                                        <Avatar sx={{ bgcolor: '#FF5722', width: 48, height: 48 }}>
                                            <Person />
                                        </Avatar>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography fontWeight={600}>
                                                {selectedProduct.seller?.brandName || selectedProduct.seller?.name || 'Unknown'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {selectedProduct.seller?.email}
                                            </Typography>
                                            {selectedProduct.seller?.phone && (
                                                <Typography variant="body2" color="text.secondary">
                                                    📱 {selectedProduct.seller.phone}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Paper>

                        {/* Product Video */}
                        {selectedProduct.video && (
                            <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                    🎥 Product Video
                                </Typography>
                                <video
                                    src={selectedProduct.video.startsWith('http') ?
                                        selectedProduct.video : `${API_URL}${selectedProduct.video}`}
                                    controls
                                    style={{ width: '100%', maxHeight: 250, borderRadius: 8 }}
                                />
                            </Paper>
                        )}

                        {/* Tags */}
                        {selectedProduct.tags && selectedProduct.tags.length > 0 && (
                            <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                    🏷️ Product Tags
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {selectedProduct.tags.map((tag, index) => (
                                        <Chip
                                            key={index}
                                            label={tag}
                                            size="small"
                                            variant="outlined"
                                            sx={{ mb: 1 }}
                                        />
                                    ))}
                                </Box>
                            </Paper>
                        )}

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', gap: 2, position: 'sticky', bottom: 0, bgcolor: 'background.paper', p: 2, borderTop: 1, borderColor: 'divider' }}>
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<Cancel />}
                                onClick={() => handleRejectClick(selectedProduct)}
                                sx={{ flex: 1 }}
                                disabled={actionLoading}
                            >
                                Reject
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={actionLoading ? <CircularProgress size={20} /> : <CheckCircle />}
                                onClick={() => handleApprove(selectedProduct)}
                                sx={{ flex: 1 }}
                                disabled={actionLoading}
                            >
                                Approve
                            </Button>
                        </Box>
                    </Box>
                )}
            </Dialog>

            {/* Reject Dialog */}
            <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)}>
                <DialogTitle>Reject Product</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Please provide a reason for rejecting "{selectedProduct?.name}":
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Reason for rejection..."
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectDialog(false)}>Cancel</Button>
                    <Button
                        onClick={handleRejectConfirm}
                        variant="contained"
                        color="error"
                        disabled={actionLoading}
                    >
                        {actionLoading ? <CircularProgress size={20} /> : 'Reject Product'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PendingProducts;
